import { Dispatch } from 'redux';
import EmailAccountController from '../controllers/EmailAccountController';
import GoogleController from '../controllers/GoogleController';
import MicrosoftController from '../controllers/MicrosoftController';
import commonHandler from '../errorHandlers/common-handler';
import { EmailAccount, NewEmailAccount } from '../interfaces/emailAccount';
import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class EmailAccountCRUDActions extends CrudActionContainer {
  constructor() {
    super('EMAIL_ACCOUNT');
  }

  getGmailOauthUrl(
    facebookPageId: number,
    redirectTo: string = window.location.href,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const result = await GoogleController.getOauthUrl(
          redirectTo,
          'gmail',
          facebookPageId,
        );
        return result.link;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getMicrosoftOauthUrl(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const result = await MicrosoftController.getOauthUrl(facebookPageId);
        return result.link;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const result = await EmailAccountController.getAccounts(facebookPageId);
        dispatch(this.retrievedAll(result.emailAccounts));
        return result;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  disconnect(emailAccount: Required<Pick<EmailAccount, 'id'>>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteLocalAction));
        await EmailAccountController.deleteAccount(emailAccount.id);
        dispatch(this.deleteLocal(emailAccount));
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(
    emailAccount: Partial<Omit<EmailAccount, 'id'>> & Pick<EmailAccount, 'id'>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const result = await EmailAccountController.updateAccount(emailAccount);
        dispatch(this.updateLocal(result));
      } catch (e) {
        commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  create(emailAccount: NewEmailAccount) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const result = await EmailAccountController.createAccount(emailAccount);
        dispatch(this.retrieved(result));
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
  navigateToGmailLogin() {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const facebookPageId = activeFacebookPageIdSelector(state);
      window.location.href = await dispatch(
        this.getGmailOauthUrl(facebookPageId),
      );
    };
  }
}

export const emailAccountCRUDActions = new EmailAccountCRUDActions();

export const useEmailAccountCRUDActions = getActionHookBuilder(
  emailAccountCRUDActions,
  [
    'getGmailOauthUrl',
    'getMicrosoftOauthUrl',
    'getAccounts',
    'disconnect',
    'update',
    'create',
    'navigateToGmailLogin',
  ],
);
